import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
   Box,
   Paper,
   Typography,
   IconButton,
   Tooltip,
   Fade,
   Zoom,
   useTheme,
   alpha,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Button,
   DialogContentText,
} from '@mui/material';
import {
   Minimize as MinimizeIcon,
   Maximize as MaximizeIcon,
   Close as CloseIcon,
   Videocam as VideocamIcon,
   OpenInFull as OpenInFullIcon,
   DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import TwilioVideoRoom from './TwilioVideoRoom';
import { useVideoCall } from '../../contexts/VideoCallContext';
import { useUser } from '../../contexts/UserContext';

const VideoCallOverlay: React.FC = () => {
   const theme = useTheme();
   const navigate = useNavigate();
   const { currentUser } = useUser();
   const {
      videoCallState,
      endVideoCall,
      minimizeCall,
      maximizeCall,
      attachVideoTrack,
      forceRecreateRemoteVideoElements,
   } = useVideoCall();

   const [isDragging, setIsDragging] = useState(false);
   const [position, setPosition] = useState({ x: 20, y: 20 });
   const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
   const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false);

   // Resize state management
   const [isResizing, setIsResizing] = useState(false);
   const [dimensions, setDimensions] = useState({ width: 450, height: 320 });
   const [resizeType, setResizeType] = useState<'corner' | 'right' | 'bottom' | null>(null);
   const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });

   // Long press handling for leave call
   const [isLongPressing, setIsLongPressing] = useState(false);
   const longPressTimer = useRef<NodeJS.Timeout>();

   // Handle mouse move for dragging
   useEffect(() => {
      const handleMouseMove = (e: MouseEvent) => {
         if (!isDragging) return;

         const newX = e.clientX - dragOffset.x;
         const newY = e.clientY - dragOffset.y;

         // Keep within screen bounds
         const maxX = window.innerWidth - dimensions.width;
         const maxY = window.innerHeight - dimensions.height;

         setPosition({
            x: Math.max(0, Math.min(newX, maxX)),
            y: Math.max(0, Math.min(newY, maxY)),
         });
      };

      const handleMouseUp = () => {
         setIsDragging(false);
      };

      if (isDragging) {
         document.addEventListener('mousemove', handleMouseMove);
         document.addEventListener('mouseup', handleMouseUp);
      }

      return () => {
         document.removeEventListener('mousemove', handleMouseMove);
         document.removeEventListener('mouseup', handleMouseUp);
      };
   }, [isDragging, dragOffset, dimensions.width, dimensions.height]);

   // Handle resize mouse events
   useEffect(() => {
      const handleMouseMove = (e: MouseEvent) => {
         if (!isResizing || !resizeType) return;

         const deltaX = e.clientX - resizeStart.x;
         const deltaY = e.clientY - resizeStart.y;

         setDimensions((prev) => {
            let newWidth = prev.width;
            let newHeight = prev.height;

            if (resizeType === 'corner' || resizeType === 'right') {
               newWidth = Math.max(
                  300,
                  Math.min(resizeStart.width + deltaX, window.innerWidth - position.x)
               );
            }
            if (resizeType === 'corner' || resizeType === 'bottom') {
               newHeight = Math.max(
                  200,
                  Math.min(resizeStart.height + deltaY, window.innerHeight - position.y)
               );
            }

            return { width: newWidth, height: newHeight };
         });
      };

      const handleMouseUp = () => {
         setIsResizing(false);
         setResizeType(null);
      };

      if (isResizing) {
         document.addEventListener('mousemove', handleMouseMove);
         document.addEventListener('mouseup', handleMouseUp);
      }

      return () => {
         document.removeEventListener('mousemove', handleMouseMove);
         document.removeEventListener('mouseup', handleMouseUp);
      };
   }, [isResizing, resizeType, resizeStart, position]);

   const handleLongPressStart = () => {
      setIsLongPressing(true);
      longPressTimer.current = setTimeout(() => {
         handleLeaveCall();
      }, 1000); // 1 second long press
   };

   const handleLongPressEnd = () => {
      setIsLongPressing(false);
      if (longPressTimer.current) {
         clearTimeout(longPressTimer.current);
      }
   };

   // Only show popup if:
   // 1. Video call is active
   // 2. User is authenticated
   // 3. We're in popup mode (not on the event page)
   if (!videoCallState.isActive || !currentUser || !videoCallState.isPopupMode) {
      return null;
   }

   // Handle mouse down for dragging
   const handleMouseDown = (e: React.MouseEvent) => {
      setIsDragging(true);
      const rect = e.currentTarget.getBoundingClientRect();
      setDragOffset({
         x: e.clientX - rect.left,
         y: e.clientY - rect.top,
      });
   };

   // Handle resize start
   const handleResizeStart = (e: React.MouseEvent, type: 'corner' | 'right' | 'bottom') => {
      e.preventDefault();
      e.stopPropagation();
      setIsResizing(true);
      setResizeType(type);
      setResizeStart({
         x: e.clientX,
         y: e.clientY,
         width: dimensions.width,
         height: dimensions.height,
      });
   };

   const handleReturnToFullscreen = () => {
      console.log('Transitioning from popup to full mode');

      // Force recreation of remote video elements before navigation
      forceRecreateRemoteVideoElements();

      // Navigate immediately - TwilioVideoRoom will handle screen share reattachment
      navigate(`/join-room/${videoCallState.roomId}?eventId=${videoCallState.eventId}`);
   };

   const handleLeaveCall = () => {
      setShowLeaveConfirmation(true);
   };

   const handleConfirmLeave = () => {
      setShowLeaveConfirmation(false);
      endVideoCall();
   };

   const handleCancelLeave = () => {
      setShowLeaveConfirmation(false);
   };

   const handleMinimize = () => {
      minimizeCall();
   };

   const handleMaximize = () => {
      maximizeCall();
   };

   const handleError = (error: Error) => {
      console.error('Video call error:', error);
   };

   // Show minimized state
   if (videoCallState.isMinimized) {
      return (
         <>
            <Fade in={true}>
               <Paper
                  elevation={8}
                  sx={{
                     position: 'fixed',
                     bottom: 20,
                     right: 20,
                     zIndex: 9999,
                     backgroundColor: isLongPressing
                        ? theme.palette.error.main
                        : theme.palette.primary.main,
                     color: 'white',
                     borderRadius: '12px',
                     cursor: 'pointer',
                     transition: 'all 0.3s ease',
                     '&:hover': {
                        transform: 'scale(1.05)',
                        backgroundColor: isLongPressing
                           ? theme.palette.error.dark
                           : theme.palette.primary.dark,
                     },
                  }}
                  onClick={handleMaximize}
                  onMouseDown={handleLongPressStart}
                  onMouseUp={handleLongPressEnd}
                  onMouseLeave={handleLongPressEnd}
                  onTouchStart={handleLongPressStart}
                  onTouchEnd={handleLongPressEnd}
               >
                  <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                     <VideocamIcon />
                     <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                        {videoCallState.eventTitle}
                        {isLongPressing && <span> - Release to Leave</span>}
                     </Typography>
                     <IconButton
                        size='small'
                        onClick={(e) => {
                           e.stopPropagation(); // Prevent maximize when clicking close
                           handleMinimize();
                        }}
                        sx={{ color: 'white', ml: 1 }}
                     >
                        <CloseIcon fontSize='small' />
                     </IconButton>
                  </Box>
               </Paper>
            </Fade>

            {/* Leave Call Confirmation Dialog */}
            <Dialog
               open={showLeaveConfirmation}
               onClose={handleCancelLeave}
               aria-labelledby='leave-dialog-title'
               aria-describedby='leave-dialog-description'
            >
               <DialogTitle id='leave-dialog-title'>Leave Video Call</DialogTitle>
               <DialogContent>
                  <DialogContentText id='leave-dialog-description'>
                     Are you sure you want to leave the video call? This will end your participation
                     in "{videoCallState.eventTitle}".
                  </DialogContentText>
               </DialogContent>
               <DialogActions>
                  <Button onClick={handleCancelLeave} color='primary'>
                     Cancel
                  </Button>
                  <Button onClick={handleConfirmLeave} color='error' variant='contained'>
                     Leave Call
                  </Button>
               </DialogActions>
            </Dialog>
         </>
      );
   }

   // Show full popup
   return (
      <>
         <Zoom in={true}>
            <Paper
               elevation={12}
               sx={{
                  position: 'fixed',
                  top: position.y,
                  left: position.x,
                  width: dimensions.width,
                  height: dimensions.height,
                  zIndex: 9999,
                  borderRadius: '16px',
                  overflow: 'hidden',
                  backgroundColor: theme.palette.background.paper,
                  border: `2px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
                  cursor: isDragging ? 'grabbing' : 'grab',
                  userSelect: 'none',
                  transition: isDragging || isResizing ? 'none' : 'all 0.3s ease',
                  '&:hover': {
                     transform: isDragging || isResizing ? 'none' : 'scale(1.02)',
                     boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.4)}`,
                  },
               }}
            >
               {/* Header */}
               <Box
                  sx={{
                     height: 40,
                     backgroundColor: theme.palette.primary.main,
                     color: 'white',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'space-between',
                     px: 1,
                     cursor: 'grab',
                  }}
                  onMouseDown={handleMouseDown}
               >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                     <DragIndicatorIcon fontSize='small' />
                     <Typography variant='caption' sx={{ fontWeight: 'bold' }}>
                        {videoCallState.eventTitle}
                     </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                     <Tooltip title='Open in fullscreen'>
                        <IconButton
                           size='small'
                           onClick={handleReturnToFullscreen}
                           sx={{ color: 'white' }}
                        >
                           <OpenInFullIcon fontSize='small' />
                        </IconButton>
                     </Tooltip>

                     <Tooltip title='Minimize'>
                        <IconButton size='small' onClick={handleMinimize} sx={{ color: 'white' }}>
                           <MinimizeIcon fontSize='small' />
                        </IconButton>
                     </Tooltip>

                     <Tooltip title='Leave call'>
                        <IconButton size='small' onClick={handleLeaveCall} sx={{ color: 'white' }}>
                           <CloseIcon fontSize='small' />
                        </IconButton>
                     </Tooltip>
                  </Box>
               </Box>

               {/* Video Content */}
               <Box
                  sx={{
                     height: 'calc(100% - 40px)',
                     position: 'relative',
                     backgroundColor: '#000',
                     overflow: 'hidden',
                  }}
               >
                  <TwilioVideoRoom
                     roomName={videoCallState.roomName}
                     eventTitle={videoCallState.eventTitle}
                     role={videoCallState.role}
                     onLeave={handleLeaveCall}
                     onError={handleError}
                     showParticipants={false}
                     enableRecording={false}
                     isPopupMode={true}
                  />
               </Box>

               {/* Resize handles */}
               {/* Corner resize handle (bottom-right) */}
               <Box
                  sx={{
                     position: 'absolute',
                     bottom: 0,
                     right: 0,
                     width: 20,
                     height: 20,
                     backgroundColor: theme.palette.primary.main,
                     cursor: 'se-resize',
                     zIndex: 10000,
                     borderBottomRightRadius: '14px', // Match the parent's border radius
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     opacity: 0.8,
                     '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                        opacity: 1,
                     },
                  }}
                  onMouseDown={(e) => handleResizeStart(e, 'corner')}
               >
                  <Box
                     sx={{
                        width: 0,
                        height: 0,
                        borderLeft: '5px solid transparent',
                        borderRight: '5px solid white',
                        borderTop: '5px solid transparent',
                        borderBottom: '5px solid white',
                     }}
                  />
               </Box>

               {/* Right edge resize handle */}
               <Box
                  sx={{
                     position: 'absolute',
                     top: 0,
                     right: 0,
                     width: 4,
                     height: '100%',
                     backgroundColor: 'transparent',
                     cursor: 'e-resize',
                     zIndex: 10000,
                     '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.3),
                     },
                  }}
                  onMouseDown={(e) => handleResizeStart(e, 'right')}
               />

               {/* Bottom edge resize handle */}
               <Box
                  sx={{
                     position: 'absolute',
                     bottom: 0,
                     left: 0,
                     width: '100%',
                     height: 4,
                     backgroundColor: 'transparent',
                     cursor: 's-resize',
                     zIndex: 10000,
                     '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.3),
                     },
                  }}
                  onMouseDown={(e) => handleResizeStart(e, 'bottom')}
               />
            </Paper>
         </Zoom>

         {/* Leave Call Confirmation Dialog */}
         <Dialog
            open={showLeaveConfirmation}
            onClose={handleCancelLeave}
            aria-labelledby='leave-dialog-title'
            aria-describedby='leave-dialog-description'
         >
            <DialogTitle id='leave-dialog-title'>Leave Video Call</DialogTitle>
            <DialogContent>
               <DialogContentText id='leave-dialog-description'>
                  Are you sure you want to leave the video call? This will end your participation in
                  "{videoCallState.eventTitle}".
               </DialogContentText>
            </DialogContent>
            <DialogActions>
               <Button onClick={handleCancelLeave} color='primary'>
                  Cancel
               </Button>
               <Button onClick={handleConfirmLeave} color='error' variant='contained'>
                  Leave Call
               </Button>
            </DialogActions>
         </Dialog>
      </>
   );
};

export default VideoCallOverlay;
